import { motion } from 'framer-motion'
import { useState } from 'react'
import { ArrowRight, Check, Loader2, Sparkles, Gift, Bell } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { sendWaitlistNotification } from '../utils/emailService'

const Waitlist = () => {
  const { t } = useLanguage()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')

    // Store in localStorage
    localStorage.setItem('azrefy_waitlist_email', email)
    
    // Send email notification to contact@azrefy.com
    await sendWaitlistNotification(email)

    setStatus('success')
    setEmail('')

    // Reset status after 5 seconds to show the success message longer
    setTimeout(() => {
      setStatus('idle')
    }, 5000)
  }

  return (
    <section id="waitlist" className="py-12 lg:py-16 bg-black">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gray-900 backdrop-blur-sm rounded-3xl p-12 md:p-16 shadow-xl border border-gray-800"
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-5xl font-bold mb-6 text-white">{t('waitlist.title')}</h2>
            <p className="text-xl text-gray-400 mb-8">
              {t('waitlist.description')}
            </p>

            {status === 'success' ? (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
                className="py-12 mb-8 relative"
              >
                {/* Floating celebration particles */}
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute"
                    initial={{ 
                      x: 0, 
                      y: 0, 
                      opacity: 0,
                      scale: 0 
                    }}
                    animate={{ 
                      x: [0, (i % 2 ? 100 : -100) * (Math.random() + 0.5)],
                      y: [0, -150 * (Math.random() + 0.5)],
                      opacity: [0, 1, 0],
                      scale: [0, 1.5, 0],
                      rotate: [0, 360 * (i % 2 ? 1 : -1)]
                    }}
                    transition={{ 
                      duration: 2,
                      delay: i * 0.1,
                      ease: 'easeOut'
                    }}
                    style={{
                      left: '50%',
                      top: '50%'
                    }}
                  >
                    <Sparkles className={`w-6 h-6 ${i % 3 === 0 ? 'text-yellow-400' : i % 3 === 1 ? 'text-green-400' : 'text-blue-400'}`} />
                  </motion.div>
                ))}
                
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/50 relative z-10"
                >
                  <Check className="w-12 h-12 text-white" strokeWidth={3} />
                </motion.div>
                
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-3xl font-bold text-white mb-4"
                >
                  🎉 Willkommen an Bord!
                </motion.h3>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-lg text-gray-300 mb-6"
                >
                  Du bist jetzt auf unserer exklusiven Waitlist! Wir senden dir eine E-Mail, sobald Azrefy live geht.
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/50 rounded-xl"
                >
                  <Sparkles className="w-5 h-5 text-green-400" />
                  <span className="text-green-300 font-medium">Bestätigungs-E-Mail wurde versendet</span>
                </motion.div>
              </motion.div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 mb-8">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('waitlist.email')}
                    required
                    disabled={status === 'loading'}
                    className="flex-1 px-6 py-4 border-2 border-gray-700 bg-gray-800 text-white rounded-xl text-lg focus:border-primary focus:outline-none transition-colors disabled:bg-gray-700 placeholder:text-gray-500"
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="gradient-primary text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl hover:-translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[200px]"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 className="animate-spin" size={20} />
                        {t('waitlist.submit')}...
                      </>
                    ) : (
                      <>
                        {t('waitlist.submit')}
                        <ArrowRight size={20} />
                      </>
                    )}
                  </button>
                </form>

                <p className="text-sm text-gray-500 mb-12">
                  🔒 Wir respektieren deine Privatsphäre. Keine Spam-Mails, versprochen.
                </p>
              </>
            )}

            {/* Benefits */}
            <div className="grid md:grid-cols-3 gap-4 text-left">
              {[
                {
                  icon: Sparkles,
                  text: '100% Kostenlos - Für Immer',
                  gradient: 'from-blue-500 to-cyan-500',
                  bgGradient: 'from-blue-500/10 to-cyan-500/10',
                },
                {
                  icon: Gift,
                  text: 'Early Access zum Beta-Launch',
                  gradient: 'from-purple-500 to-pink-500',
                  bgGradient: 'from-purple-500/10 to-pink-500/10',
                },
                {
                  icon: Bell,
                  text: 'Exklusive Updates & Insider-Einblicke',
                  gradient: 'from-orange-500 to-red-500',
                  bgGradient: 'from-orange-500/10 to-red-500/10',
                },
              ].map((benefit, index) => {
                const Icon = benefit.icon
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="relative group"
                  >
                    {/* Gradient glow on hover */}
                    <div className={`absolute -inset-0.5 bg-gradient-to-r ${benefit.gradient} rounded-xl blur opacity-0 group-hover:opacity-30 transition duration-300`} />
                    
                    {/* Card */}
                    <div className={`relative flex items-start gap-3 p-4 bg-gradient-to-br ${benefit.bgGradient} border border-white/10 rounded-xl backdrop-blur-sm`}>
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${benefit.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                        <Icon size={20} className="text-white" />
                      </div>
                      <span className="text-gray-200 font-medium text-sm leading-relaxed pt-1.5">
                        {benefit.text}
                      </span>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Waitlist
